import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder, DraftOrderLine } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyCustomLineForm,
  fromCustomLine,
  linesInput,
  toCustomLineBody,
  toCustomLineInput,
  withLineAdded,
} from '../api/mappers';
import { useUpdateDraftLine, useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { editLineFieldPath, isStateConflict, lineFieldPath } from '../schemas/draft-order-rules';
import {
  customLineSchema,
  type CustomLineFormInput,
  type CustomLineFormOutput,
} from '../schemas/line-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['title', 'sku', 'quantity', 'unitPrice']);

export interface CustomLineDialogProps {
  storeId: string;
  draft: DraftOrder;
  /** The custom line to change in place; without it the dialog adds a new one. */
  line?: DraftOrderLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A typed-in line; the price must be in the store currency or the backend answers 422. */
export function CustomLineDialog({
  storeId,
  draft,
  line,
  open,
  onOpenChange,
}: CustomLineDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const add = useUpdateDraftOrder(storeId, 'draftOrders:lines.saved');
  const edit = useUpdateDraftLine(storeId, 'draftOrders:lines.saved');
  const pending = add.isPending || edit.isPending;
  const form = useForm<CustomLineFormInput, unknown, CustomLineFormOutput>({
    resolver: useI18nResolver(customLineSchema),
    defaultValues: line ? fromCustomLine(line) : emptyCustomLineForm(),
  });

  const close = () => {
    onOpenChange(false);
    add.reset();
    edit.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    const index = line
      ? draft.lines.findIndex((l) => l.id === line.id)
      : linesInput(draft.lines).length;
    try {
      if (line) {
        await edit.mutateAsync({ id: draft.id, lineId: line.id, body: toCustomLineBody(values) });
      } else {
        await add.mutateAsync({
          id: draft.id,
          body: { lines: withLineAdded(draft.lines, toCustomLineInput(values)) },
        });
      }
      close();
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        knownFields: KNOWN_FIELDS,
        mapField: (field) =>
          (line ? editLineFieldPath(field, index, []) : lineFieldPath(field, index, [])) ??
          'root.server',
      });
      if (applied) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {line
              ? t('draftOrders:customLine.editTitle', { title: line.title })
              : t('draftOrders:customLine.title')}
          </DialogTitle>
          <DialogDescription>{t('draftOrders:customLine.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:customLine.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:customLine.sku')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:customLine.quantity')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={1} max={99} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="unitPrice"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:customLine.unitPrice')}</FormLabel>
                  <FormControl>
                    <MoneyInput
                      currency={draft.currency}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="requiresShipping"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('draftOrders:customLine.requiresShipping')}</FormLabel>
                    <FormDescription>
                      {t('draftOrders:customLine.requiresShippingHint')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending
                  ? t('draftOrders:customLine.submitting')
                  : line
                    ? t('draftOrders:customLine.save')
                    : t('draftOrders:customLine.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
